import React, { FC, ReactNode } from 'react';

import classnames from 'classnames';
import { Icon as MintelIcon, Tooltip, Text } from 'mintel-react-ui';
import { mintelBlue200, mintelWhite } from 'mintel-tokens';

import styles from './CollapsedNav.scss';
import useTranslate from '../../utils/translations';
import useTracking from '../../utils/useTracking';
import Icon from '../Icon';
import trendsNavIcon from './RelatedContentNav/relatedContentIcon.svg';

type TooltipProps = {
  tooltipText: string;
  trigger: JSX.Element;
};

const NavTooltip: FC<TooltipProps> = ({ trigger, tooltipText }) => {
  return (
    <Tooltip.Hover placement={'right'} short trigger={trigger}>
      <div className={styles.tooltip}>
        <Text.Body>{tooltipText}</Text.Body>
      </div>
    </Tooltip.Hover>
  );
};

const NavItem: FC<{
  tooltipText: string;
  expandedIcon: string;
  title: string;
  handleClick: (title: string) => void;
  children: ReactNode;
}> = ({ tooltipText, expandedIcon, title, handleClick, children }) => {
  return (
    <NavTooltip
      tooltipText={tooltipText || title}
      trigger={
        <div
          className={styles['outer-item']}
          data-intercom-target="cpa-translations"
        >
          <button
            className={classnames(styles.navButton, {
              [styles.expanded]: expandedIcon === title,
            })}
            tabIndex={0}
            onClick={(): void => {
              handleClick(title);
            }}
            aria-label={title}
            aria-expanded={expandedIcon === title}
            aria-controls={`${title.replace(/\s+/g, '-')}-nav-content`}
          >
            {children}
          </button>
        </div>
      }
    />
  );
};
type CollapsedNavPops = {
  databookUrl?: string;
  expandedIcon: string;
  navTitles: {
    [key: string]: string;
  };
  showSeriesData: boolean;
  setExpandedIcon: (iconTitle: string) => void;
  showTranslations: boolean;
  showToc: boolean;
  showRelatedContent: boolean;
};

const CollapsedNav: FC<CollapsedNavPops> = ({
  databookUrl = '',
  expandedIcon,
  navTitles,
  showSeriesData,
  setExpandedIcon,
  showTranslations,
  showToc,
  showRelatedContent,
}) => {
  const {
    trackDatabookLink,
    trackOpeningInPageNav,
    trackSwitchingTabsInInPageNav,
    trackClosingInPageNav,
  } = useTracking();

  const t = useTranslate();
  const languageNavTooltipText = t(
    'This report is available in other languages',
  );

  const handleClick = (whichIcon: string): void => {
    if (expandedIcon) {
      expandedIcon !== whichIcon
        ? trackSwitchingTabsInInPageNav()
        : trackClosingInPageNav();
    } else {
      trackOpeningInPageNav();
    }
    whichIcon = expandedIcon === whichIcon ? '' : whichIcon;
    setExpandedIcon(whichIcon);
  };

  return (
    <div className={styles['collapsed-nav']}>
      {showTranslations && (
        <NavItem
          tooltipText={languageNavTooltipText}
          expandedIcon={expandedIcon}
          title={navTitles.translation}
          handleClick={handleClick}
        >
          <Icon name="Translation" />
        </NavItem>
      )}
      {showToc && (
        <NavItem
          tooltipText={navTitles.toc}
          expandedIcon={expandedIcon}
          title={navTitles.toc}
          handleClick={handleClick}
        >
          <MintelIcon name="menu" color={mintelWhite} size={22} />
        </NavItem>
      )}
      {databookUrl && (
        <NavTooltip
          tooltipText={navTitles.databook}
          trigger={
            <div
              className={styles['outer-item']}
              data-intercom-target="cpa-view-databook"
            >
              <a
                className={classnames(
                  styles.navButton,
                  styles['nav-link'],
                  styles['mintel-icon'],
                )}
                href={databookUrl}
                onClick={(): void => trackDatabookLink(databookUrl)}
                rel="noreferrer"
                target="_blank"
                aria-label={navTitles.databook}
              >
                <MintelIcon
                  name="databook"
                  color={mintelBlue200}
                  size={20}
                  testId="navIcon"
                />
              </a>
            </div>
          }
        />
      )}
      {showSeriesData && (
        <NavItem
          tooltipText={navTitles.series}
          expandedIcon={expandedIcon}
          title={navTitles.series}
          handleClick={handleClick}
        >
          <MintelIcon name="calendar" color={mintelWhite} size={22} />
        </NavItem>
      )}
      {showRelatedContent && (
        <NavItem
          tooltipText={navTitles.relatedContent}
          expandedIcon={expandedIcon}
          title={navTitles.relatedContent}
          handleClick={handleClick}
        >
          <MintelIcon svg={trendsNavIcon} size={22} />
        </NavItem>
      )}
    </div>
  );
};

export default CollapsedNav;
